import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  Zap,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  BarChart2,
  BarChart3,
  Upload,
  LayoutGrid,
} from 'lucide-react';

const navItems = [
  { path: '/app/portfolio-pulse', icon: BarChart2,   label: 'Portfolio Pulse' },
  { path: '/app/portfolio-board', icon: LayoutGrid,  label: 'IT Programs Board' },
  { path: '/app/it-metrics',      icon: BarChart3,   label: 'IT Scorecard' },
  { path: '/app/upload-download', icon: Upload,      label: 'Upload / Download' },
];

const Sidebar = () => {
  const { theme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(true);

  return (
    <aside
      className={`${collapsed ? 'w-16' : 'w-64'} h-screen sticky top-0 flex flex-col bg-[#1A1A1A] border-r border-[#00C26D]/15 transition-all duration-200 overflow-hidden shrink-0`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="px-3 py-4 border-b border-white/10 shrink-0">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-[#00C26D] rounded-xl flex items-center justify-center shadow-lg shadow-[#00C26D]/20 shrink-0">
              <Zap className="w-6 h-6 text-white" />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <h1 className="font-bold text-white text-lg tracking-tight truncate">INFOBLOX</h1>
                <p className="text-[10px] text-[#00D97E]/60 font-medium tracking-wider uppercase">IT Command Center</p>
              </div>
            )}
          </div>
          {!collapsed && (
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors shrink-0"
              data-testid="theme-toggle"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light'
                ? <Moon className="w-5 h-5 text-slate-400" />
                : <Sun className="w-5 h-5 text-[#00D97E]" />}
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1">
        {navItems.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              `relative flex items-center ${collapsed ? 'justify-center py-3 px-0' : 'gap-3 px-4 py-3'} rounded-lg font-medium transition-all ${
                isActive
                  ? 'bg-[#00C26D]/15 text-[#00D97E]'
                  : 'text-slate-400 hover:bg-white/10 hover:text-white'
              }`
            }
            data-testid={`nav-${item.path.slice(1)}`}
          >
            {({ isActive }) => (
              <>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#00C26D] rounded-r-full transition-opacity"
                  style={{ opacity: isActive ? 1 : 0 }} />
                <item.icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-[#00C26D]' : ''}`} />
                {!collapsed && <span className="truncate">{item.label}</span>}
                {!collapsed && isActive && <ChevronRight className="w-4 h-4 ml-auto shrink-0 text-[#00C26D]" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-2 border-t border-white/10 space-y-1 shrink-0">
        {collapsed && (
          <button
            onClick={toggleTheme}
            className="w-full flex items-center justify-center py-3 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="theme-toggle"
            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          >
            {theme === 'light'
              ? <Moon className="w-5 h-5 text-slate-400" />
              : <Sun className="w-5 h-5 text-[#00D97E]" />}
          </button>
        )}
        <button
          onClick={() => setCollapsed(c => !c)}
          className={`w-full flex items-center ${collapsed ? 'justify-center py-3 px-0' : 'gap-3 px-4 py-3'} rounded-lg text-slate-400 hover:bg-white/10 hover:text-white transition-all font-medium`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed
            ? <ChevronRight className="w-5 h-5" />
            : <><ChevronLeft className="w-5 h-5" /><span>Collapse</span></>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
